import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/cn'

export interface TpSlValues {
  profit: string
  rate: string
  balancePct: string
}

interface TpSlPanelProps {
  kind: 'tp' | 'sl'
  open: boolean
  onToggle: () => void
  values: TpSlValues
  onChange: (next: TpSlValues) => void
  className?: string
}

/**
 * Collapsible Take Profit / Stop Loss block
 * (Profit $, Rate, Balance %) — matches institutional ticket UX.
 */
export function TpSlPanel({
  kind,
  open,
  onToggle,
  values,
  onChange,
  className,
}: TpSlPanelProps) {
  const { t } = useTranslation('trading')
  const title =
    kind === 'tp' ? t('orderTicket.takeProfit') : t('orderTicket.stopLoss')
  const accent = kind === 'tp' ? 'text-bid' : 'text-ask'

  const set = (key: keyof TpSlValues, value: string) => {
    onChange({ ...values, [key]: value })
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-sm border border-terminal-border/40',
        className,
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'flex h-8 w-full items-center justify-between gap-2 px-2.5 text-xs font-semibold',
          'bg-terminal-elevated/60 hover:bg-terminal-elevated',
          accent,
        )}
        aria-expanded={open}
      >
        <span>{title}</span>
        <Chevron open={open} />
      </button>

      {open ? (
        <div className="space-y-2 border-t border-terminal-border/40 bg-terminal-surface p-2.5">
          <SuffixField
            value={values.profit}
            onChange={(v) => set('profit', v)}
            suffix="$"
            label={t('orderTicket.tpProfit')}
            hint={t('orderTicket.tpProfitHint')}
          />
          <SuffixField
            value={values.rate}
            onChange={(v) => set('rate', v)}
            label={t('orderTicket.tpRate')}
            hint={t('orderTicket.tpRateHint')}
          />
          <SuffixField
            value={values.balancePct}
            onChange={(v) => set('balancePct', v)}
            suffix="%"
            label={t('orderTicket.tpBalance')}
            hint={t('orderTicket.tpBalanceHint')}
          />
        </div>
      ) : null}
    </div>
  )
}

function SuffixField({
  value,
  onChange,
  suffix,
  label,
  hint,
}: {
  value: string
  onChange: (v: string) => void
  suffix?: string
  label: string
  hint: string
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative min-w-0 flex-1">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          inputMode="decimal"
          className={cn(
            'h-8 w-full rounded-md border border-terminal-border/50 bg-terminal-bg',
            'px-2.5 font-mono text-xs tabular-nums text-terminal-fg',
            suffix && 'pe-7',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
          )}
        />
        {suffix ? (
          <span className="pointer-events-none absolute inset-y-0 end-2 flex items-center text-[11px] text-terminal-muted">
            {suffix}
          </span>
        ) : null}
      </div>
      <label className="flex shrink-0 items-center gap-1 text-[11px] text-terminal-muted">
        {label}
        <span
          className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-terminal-border text-[8px]"
          title={hint}
        >
          i
        </span>
      </label>
    </div>
  )
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      className={cn('h-3 w-3 transition-transform', open && 'rotate-180')}
      fill="none"
      aria-hidden
    >
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function useTpSlState() {
  const [tpOpen, setTpOpen] = useState(false)
  const [slOpen, setSlOpen] = useState(false)
  const [tp, setTp] = useState<TpSlValues>({
    profit: '0',
    rate: '',
    balancePct: '',
  })
  const [sl, setSl] = useState<TpSlValues>({
    profit: '0',
    rate: '',
    balancePct: '',
  })
  return {
    tpOpen,
    slOpen,
    toggleTp: () => setTpOpen((o) => !o),
    toggleSl: () => setSlOpen((o) => !o),
    tp,
    sl,
    setTp,
    setSl,
  }
}
